import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import NodeChildrenOnly from '@adsk/bim360-matrix-react-node-children-only';

import './_StickyScrollableContainer.scss';

const HEADER_COMPONENT_NAME = 'StickyScrollableContainer.Header';
const CONTENT_COMPONENT_NAME = 'StickyScrollableContainer.Content';

/**
 * @class StickyScrollableContainer is a component which serves as a container which makes the header part of it sticky
 * when scrolling vertically the content part
 */
export default class StickyScrollableContainer extends Component {
  /**
   * constructor
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = { scrollOccurred: false };
    this._handleScroll = this._handleScroll.bind(this);
  }

  /**
   * Handle scroll event on the container
   * @param event
   * @private
   */
  _handleScroll(event) {
    if (event && event.target) {
      const scrollOccurred = event.target.scrollTop > 0;

      // scrollOccurred has two states, scrolled (position>0) or not scrolled.
      // We're using the following if block to flag scrollOccurred and run setState only on change
      if (this.state.scrollOccurred !== scrollOccurred) {
        this.setState({ scrollOccurred });
      }
    }
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    if (this._contentElement) {
      this._contentElement.addEventListener('scroll', this._handleScroll);
    }
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    if (this._contentElement) {
      this._contentElement.removeEventListener('scroll', this._handleScroll);
    }
  }

  renderFooter(footerChild) {
    let footerElement;
    if (footerChild) {
      const footerClasses = classNames(
        'StickyScrollableContainer__footer',
        footerChild.props.cssClass || footerChild.props.className
      );
      footerElement = <div className={footerClasses}>{footerChild.props.children}</div>;
    }
    return footerElement;
  }

  getContentElementRef() {
    return this._contentElement;
  }

  /**
   * render
   * @returns {VDOM}
   */
  render() {
    const headerChild = this.props.children[0];
    const contentChild = this.props.children[1];
    const footerChild = this.props.children.length > 2 ? this.props.children[2] : undefined;

    const containerClasses = classNames('StickyScrollableContainer', this.props.className);

    const headerClasses = classNames(
      'StickyScrollableContainer__header',
      headerChild.props.cssClass || headerChild.props.className,
      {
        [headerChild.props.cssClass || headerChild.props.className + '--scroll']: this.state
          .scrollOccurred
      }
    );

    const contentClasses = classNames(
      'StickyScrollableContainer__content',
      contentChild.props.cssClass || contentChild.props.className
    );

    return (
      <div className={containerClasses}>
        <div className={headerClasses}>{headerChild.props.children}</div>
        <div className={contentClasses} ref={elem => (this._contentElement = elem)}>
          {contentChild.props.children}
        </div>
        {this.renderFooter(footerChild)}
      </div>
    );
  }
}

StickyScrollableContainer.propTypes = {
  /**
   * A custom className for the component container
   */
  className: PropTypes.string,
  /**
   * The Scrollable container parts - StickyScrollableContainer.Header / StickyScrollableContainer.Content / StickyScrollableContainer.Footer
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

/**
 * Define the Header component of container. This component just renders its children and does not have a wrapper element.
 */
StickyScrollableContainer.Header = NodeChildrenOnly(HEADER_COMPONENT_NAME, {
  className: PropTypes.string
});

/**
 * Define the Content component of container. This component just renders its children and does not have a wrapper element.
 */
StickyScrollableContainer.Content = NodeChildrenOnly(CONTENT_COMPONENT_NAME, {
  className: PropTypes.string
});
/**
 * Define the Footer component of container. This component just renders its children and does not have a wrapper element.
 */
StickyScrollableContainer.Footer = NodeChildrenOnly(CONTENT_COMPONENT_NAME, {
  className: PropTypes.string
});
